<template>
  <div id="ai-analysis-report" class="ai-report">
    <!-- Back + breadcrumb. Variant C of src/assets/showcase/ai-analysis-topline-showcase.html (Khalid, 2026-09-07):
         a 44px tonal button so the way out reads as a control, and crumb segments that actually navigate. -->
    <div class="topline">
      <v-btn large outlined color="primary" class="topline__back" @click="back">
        <v-icon left>mdi-arrow-left</v-icon>
        Back to AI Analysis
      </v-btn>
      <nav class="crumbs" aria-label="Breadcrumb">
        <ol>
          <!-- Section label, not a link: the hub has no route of its own. `inspection` is tab index 0
               (dashboard.routes.js:32, views/index.vue:119), a sibling of AI Analysis, so linking there would
               send people to a different dashboard. -->
          <li><span class="crumb-section">Analysis</span></li>
          <li class="crumb-sep" aria-hidden="true"><v-icon small>mdi-chevron-right</v-icon></li>
          <li><router-link class="crumb-link" :to="listRoute">AI Analysis</router-link></li>
          <li class="crumb-sep" aria-hidden="true"><v-icon small>mdi-chevron-right</v-icon></li>
          <li>
            <span class="crumb-current" aria-current="page">{{ crumbJob }}<span v-if="crumbKind" class="crumb-kind"> · {{ crumbKind }}</span></span>
          </li>
        </ol>
      </nav>
    </div>

    <div v-if="!job && jobsLoading" class="card state-block" aria-busy="true" aria-live="polite">
      <v-progress-circular indeterminate :size="28" :width="3" color="primary" class="mb-3" />
      <p>Loading the analysis job…</p>
    </div>

    <div v-else-if="!job" class="card state-block" role="alert">
      <div class="state-block__icon"><v-icon :size="30">mdi-file-search-outline</v-icon></div>
      <h3>Analysis job not found</h3>
      <p>The job may belong to another facility or has been removed. Go back to the AI Analysis list to pick one.</p>
    </div>

    <div v-else-if="!isDone" class="card state-block">
      <div class="state-block__icon"><v-icon :size="30">{{ notDoneState.icon }}</v-icon></div>
      <div class="state-block__chip"><job-status-chip :job="jobRow" /></div>
      <h3>{{ notDoneState.title }}</h3>
      <p>{{ notDoneState.text }}</p>
    </div>

    <template v-else>
      <report-header
        :job-label="jobLabel"
        :job-sub="jobSub"
        :ai-model="aiModel"
        :model-sub="modelSub"
        :create-date="job.createDate"
        :review-status="reviewStatus"
        :decided-count="decided"
        :total-count="findings.length" />

      <!-- Body grid (Khalid, 2026-09-06): the left column stretches to the viewer's height, the findings card fills the rest
           of it, the detail panel opens beside the table inside that card, the right column is the shared viewers section. -->
      <div class="report-body" :class="{ 'report-body--panel-open': !!activeFinding }">
        <div class="report-left">
          <report-kpis :kpis="kpis" />
          <findings-table
            :findings="findings"
            :size-by-id="sizeById"
            :images-by-id="imagesById"
            :selected.sync="selected"
            :active-id="activeId"
            :loading="findingsLoading"
            :busy="busy"
            :density.sync="density"
            @focus="open"
            @approve="approve"
            @reject="reject"
            @details="open"
            @approve-selected="approveMany"
            @reject-selected="rejectMany"
            @link="openPicker([$event.id])"
            @unlink="unlinkFindings([$event.id])"
            @link-selected="openPicker($event)"
            @unlink-selected="unlinkFindings($event)">
            <template #panel>
              <detection-detail-panel
                v-if="activeFinding"
                :finding="activeFinding"
                :image-size="sizeById[activeFinding.imageId] || {}"
                :scan-name="scanNameOf(activeFinding)"
                :index="activeIndex"
                :count="findings.length"
                :busy="busy"
                :suggested-tag="suggestedTag"
                @link="openPicker([$event.id])"
                @unlink="unlinkFindings([$event.id])"
                @link-suggested="linkSuggested"
                @close="closePanel"
                @step="stepPanel"
                @approve="approve"
                @reject="reject"
                @show-on-scan="showOnScan"
                @report="openFindingReport" />
            </template>
          </findings-table>
        </div>

        <!-- Ticket 09: the same viewers section as the other Analysis tabs; findings reach it through the ticket 05
             `inspects` prop, never the shared AI_INSPECT/ISOLATED_INSPECTS slot a kept-alive AI Inspect page watches. -->
        <section class="card viewer-card" :class="{ 'viewer-card--expanded': viewerExpanded }" aria-labelledby="imageTitle">
          <h2 class="card-title" id="imageTitle">
            <span class="ttl">Annotated image</span>
            <span class="scope-suffix">({{ scopeLabel }})</span>
            <span class="spacer"></span>
            <span v-if="scanOptions.length > 1" class="scan-select">
              <v-select
                :value="currentImageId"
                :items="scanOptions"
                dense
                outlined
                hide-details
                label="Scan"
                class="scan-select__field"
                :menu-props="{ offsetY: true }"
                @change="selectScan" />
            </span>
          </h2>
          <div class="viewer-card__stage">
            <viewers-section
              ref="viewersSection"
              :viewer-tab.sync="viewerTab"
              :inspects="viewerInspects"
              :enabled-tabs="viewerTabs"
              :feature="viewerFeature"
              @inspect-selected="onPolygonSelected"
              @expand="toggleViewerExpanded"
              @closeViewersSection="onViewerClose" />
          </div>
        </section>
      </div>

      <report-footer :review-status="reviewStatus" />

      <div class="print-footnote">Printed from AVA · AI Analysis job report · {{ jobLabel }} · AI proposal, not a validated inspection result.</div>

      <report-action-bar
        :pending-count="pending.length"
        :busy="busy || exporting"
        @accept-all="acceptAll"
        @reject-all="rejectAll"
        @export="exportPdf" />

      <!-- Link finding(s) to a component (approved showcase ai-analysis-tag-picker-showcase.html, option B). -->
      <component-picker-dialog
        v-model="picker.open"
        :findings="pickerFindings"
        :facility-id="facilityId"
        :suggested="suggestedTag"
        :recent="recentComponents"
        :busy="linking"
        @link="linkTo"
        @unlink="unlinkFromPicker" />
    </template>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { $formatDate } from '@/utils';
  import { error } from '@/utils/debugLogger';
  import { APPROVAL, JOB_UI_STATUS, toUiStatus } from '../utils/findingsMetrics';
  import { JOB_TYPES, splitImagesIds } from '../utils/jobTree';
  import { shortJobId } from '../utils/jobsFormat';
  import { EM_DASH, RING_BANDS, decidedCount, suggestedTagFor, pendingFindings, ringBand, viewerInspectsFor } from '../utils/reportFormat';
  import { linkedCount, pushRecent } from '../utils/findingComponents';
  import { readDensity, storeDensity } from '../components/aiAnalysis/densityPreference';
  import { REPORT_SCOPE, FINDING_REPORT_MOCK_MESSAGE, DATE_FORMAT, ACCEPTED_FINDINGS_NOTE } from '../components/aiAnalysis/reportConstants';
  import ViewersSection from '../components/ViewersSection.vue';
  import JobStatusChip from '../components/aiAnalysis/JobStatusChip.vue';
  import ReportHeader from '../components/aiAnalysis/ReportHeader.vue';
  import ReportKpis from '../components/aiAnalysis/ReportKpis.vue';
  import FindingsTable from '../components/aiAnalysis/FindingsTable.vue';
  import DetectionDetailPanel from '../components/aiAnalysis/DetectionDetailPanel.vue';
  import ReportFooter from '../components/aiAnalysis/ReportFooter.vue';
  import { generateReportPdf } from '../utils/reportPdf';
  import ReportActionBar from '../components/aiAnalysis/ReportActionBar.vue';
  import ComponentPickerDialog from '../components/aiAnalysis/ComponentPickerDialog.vue';

  const DENSITY_STORAGE_KEY = 'aiva.aiAnalysis.findingsDensity';
  const DEFAULT_IMAGE_WIDTH = 4057;
  const DEFAULT_IMAGE_HEIGHT = 2028;
  const REJECT_NOTE = 'removed from the report and cannot be restored';
  const UNLINK_NOTE = 'They will no longer be grouped or reported under that component.';
  const VIEWER_360 = '360 View';
  // No Document tab: ViewersSection auto-switches to it whenever TAGS.activeDocument changes elsewhere.
  const VIEWER_TABS = Object.freeze(['3D View', '2D View', VIEWER_360]);
  const VIEWER_FEATURE = 'ai-analysis';
  const MODEL_SUB = Object.freeze({
    [JOB_TYPES.AVA_INSPECT_CONSOLIDATED]: 'Multi-view consolidation',
    [JOB_TYPES.AVA_INSPECT_SINGLE]: 'Single snap',
    [JOB_TYPES.FULL_SCAN]: 'High Accuracy',
  });
  const NOT_DONE_STATES = Object.freeze({
    [JOB_UI_STATUS.RUNNING]: { icon: 'mdi-progress-clock', title: 'Analysis still running', text: 'The report opens when the analysis completes. The AI Analysis list refreshes itself while a job runs.' },
    [JOB_UI_STATUS.FAILED]: { icon: 'mdi-alert-circle-outline', title: 'Analysis failed', text: 'No report: the analysis did not complete. Run it again from the scan or with Run New Analysis.' },
    [JOB_UI_STATUS.CANCELLED]: { icon: 'mdi-cancel', title: 'Analysis cancelled', text: 'No report: the analysis was cancelled before it completed.' },
  });

  export default {
    name: 'AIAnalysisReport',
    components: { ViewersSection, JobStatusChip, ReportHeader, ReportKpis, FindingsTable, DetectionDetailPanel, ReportFooter, ReportActionBar, ComponentPickerDialog },
    data() {
      return {
        selected: [],
        // One source of truth: the active row, the highlighted polygon and the open detail panel.
        activeId: null,
        busy: false,
        exporting: false,
        density: readDensity(DENSITY_STORAGE_KEY),
        scopeLabel: REPORT_SCOPE.toLowerCase(),
        viewerTab: VIEWER_360,
        viewerTabs: VIEWER_TABS,
        viewerFeature: VIEWER_FEATURE,
        viewerImageId: null,
        viewerExpanded: false,
        // Component picker: which findings it is open for; `recentComponents` is the "Recent" strip for this report.
        picker: { open: false, ids: [] },
        recentComponents: [],
        linking: false,
      };
    },
    computed: {
      ...mapGetters('AI_ANALYSIS', {
        jobById: 'JOB_BY_ID',
        findingsForJob: 'FINDINGS_FOR_JOB',
        findingsLoadingJobId: 'FINDINGS_LOADING_JOB_ID',
        kpisForJob: 'KPIS_FOR_JOB',
        reviewStatusForJob: 'REVIEW_STATUS_FOR_JOB',
        imagesById: 'IMAGES_BY_ID',
        avaInspectsById: 'AVA_INSPECTS_BY_ID',
        jobsLoading: 'JOBS_LOADING',
      }),
      facilityId() {
        return this.$route.params.id;
      },
      jobId() {
        return this.$route.params.jobId;
      },
      job() {
        return this.jobById(this.jobId);
      },
      jobRow() {
        return { ...this.job, uiStatus: toUiStatus(this.job.status) };
      },
      isDone() {
        return !!this.job && toUiStatus(this.job.status) === JOB_UI_STATUS.DONE;
      },
      notDoneState() {
        return NOT_DONE_STATES[toUiStatus(this.job.status)] || NOT_DONE_STATES[JOB_UI_STATUS.FAILED];
      },
      findings() {
        return this.findingsForJob(this.jobId);
      },
      findingsLoading() {
        return this.findingsLoadingJobId === this.jobId;
      },
      kpis() {
        return this.kpisForJob(this.jobId);
      },
      reviewStatus() {
        return this.reviewStatusForJob(this.jobId);
      },
      pending() {
        return pendingFindings(this.findings);
      },
      decided() {
        return decidedCount(this.findings);
      },
      aiModel() {
        return (this.findings.find((f) => f.aiModel) || {}).aiModel || '';
      },
      modelSub() {
        return MODEL_SUB[this.job.jobType] || MODEL_SUB[JOB_TYPES.FULL_SCAN];
      },
      sizeById() {
        return Object.fromEntries(
          Object.values(this.imagesById).map((i) => [i.id, { width: i.width || DEFAULT_IMAGE_WIDTH, height: i.height || DEFAULT_IMAGE_HEIGHT }]),
        );
      },
      scanIds() {
        return this.job ? splitImagesIds(this.job.imagesIds) : [];
      },
      /**
       * Everything utils/reportPdf.js needs, resolved here because the view already owns the job, the images
       * and the badge-ordered findings. `findings` is every finding of the job that is not rejected, so the
       * table, the cards and the summary match the screen; the annotated image is the first scan that has
       * findings and carries only that scan's contours (`imageFindings`).
       */
      pdfModel() {
        const scan = this.exportScans[0] || {};
        const others = this.exportScans.length - 1;
        return {
          jobLabel: this.jobLabel,
          jobSub: this.jobSub,
          aiModel: this.aiModel,
          modelSub: this.modelSub,
          dateLabel: $formatDate(this.job.createDate, DATE_FORMAT),
          reviewStatus: this.reviewStatus,
          decidedCount: this.decided,
          totalCount: this.findings.length,
          approved: this.findings.length > 0 && this.decided === this.findings.length,
          scanName: scan.name || '',
          imageLabel: others > 0 ? `${scan.name} (+${others} more)` : scan.name || '',
          imageUrl: (scan.image || {}).imageUrl || null,
          imageFindings: scan.findings || [],
          findings: this.findings.filter((f) => f.approvalStatus !== APPROVAL.REJECTED),
          sizeById: this.sizeById,
        };
      },
      /**
       * One entry per scan that still has a finding to show, for the exported PDF. Rejected findings are
       * already out of `viewerInspectsFor`, so a scan whose findings were all rejected is not listed.
       */
      exportScans() {
        return this.scanIds
          .map((id) => ({
            id,
            name: (this.imagesById[id] || {}).name || id,
            image: this.imagesById[id] || {},
            size: this.sizeById[id] || {},
            findings: viewerInspectsFor(this.findings, id, null),
          }))
          .filter((scan) => scan.findings.length > 0);
      },
      scanNames() {
        return this.scanIds.map((id) => (this.imagesById[id] || {}).name || id);
      },
      scanOptions() {
        return this.scanIds.map((id) => ({ value: id, text: (this.imagesById[id] || {}).name || id }));
      },
      /** Scan shown in the viewer: the picked one, else the job's first scan. */
      currentImageId() {
        return this.viewerImageId || this.scanIds[0] || null;
      },
      viewerInspects() {
        return viewerInspectsFor(this.findings, this.currentImageId, this.activeId);
      },
      activeFinding() {
        return this.findings.find((f) => f.id === this.activeId) || null;
      },
      activeIndex() {
        return this.findings.findIndex((f) => f.id === this.activeId);
      },
      avaInspect() {
        return this.job.avaInspectId ? this.avaInspectsById[this.job.avaInspectId] || null : null;
      },
      /** The AVA Inspect's own component, offered as a suggestion (never applied on its own); null for full scans. */
      suggestedTag() {
        return suggestedTagFor(this.job, this.avaInspectsById);
      },
      pickerFindings() {
        return this.picker.ids
          .map((id) => this.findings.find((f) => f.id === id))
          .filter(Boolean)
          .map((f) => ({
            ...f,
            scanName: this.scanNameOf(f) || f.scanName || f.imageName || '',
          }));
      },
      jobLabel() {
        if (this.job.avaInspectId) return (this.avaInspect && this.avaInspect.title) || 'AVA Inspect';
        const [first, ...rest] = this.scanNames;
        return first ? `${first}${rest.length ? ` +${rest.length}` : ''}` : EM_DASH;
      },
      jobSub() {
        if (this.job.avaInspectId) {
          const run = this.job.jobType === JOB_TYPES.AVA_INSPECT_CONSOLIDATED ? 'Consolidated run' : 'Single snap';
          return `AVA Inspect · ${run} · ${this.scanNames.length} ${this.scanNames.length === 1 ? 'scan' : 'scans'}`;
        }
        return `Full scan · ${shortJobId(this.job.id)}`;
      },
      /** The jobs list this report came from - a real named route (dashboard.routes.js:99), so the crumb
          segment navigates instead of reading as a caption that does nothing when clicked. */
      listRoute() {
        return { name: 'ai-analysis', params: { id: this.facilityId } };
      },
      crumbJob() {
        return this.job ? this.jobLabel : 'Job';
      },
      crumbKind() {
        if (!this.job) return '';
        return this.job.avaInspectId ? 'AVA Inspect' : 'Full scan';
      },
      imagesLoaded() {
        return Object.keys(this.imagesById).length > 0;
      },
    },
    watch: {
      jobId: { immediate: true, handler: 'load' },
      // Direct reload in a fresh session: the job list may have arrived before the scan list (see AIAnalysis.vue).
      imagesLoaded(loaded) {
        if (loaded && !this.job && !this.jobsLoading) this.load();
      },
      density(value) {
        storeDensity(DENSITY_STORAGE_KEY, value);
      },
    },
    activated() {
      this.load();
    },
    methods: {
      async load() {
        if (!this.jobId) return;
        const store = this.$store;
        // Direct reloads land here without the list view: the same three loads it performs on creation.
        if (!this.job) await store.dispatch('AI_ANALYSIS/LOAD_JOBS', this.facilityId);
        if (!Object.keys(this.avaInspectsById).length) store.dispatch('FETCH_AVA_INSPECT_TAGS', { assetId: this.facilityId });
        if (!(store.getters['AI_INSPECT/IMAGES'] || []).length) {
          const options = store.getters['AI_INSPECT/GET_OPTIONS'];
          store.commit('AI_INSPECT/SET_OPTIONS', { ...options, facilityId: this.facilityId });
          store.dispatch('AI_INSPECT/GET_IMAGES');
        }
        this.selected = [];
        this.activeId = null;
        // Recent components belong to a facility; a kept-alive switch to another facility must not offer stale ids.
        this.recentComponents = [];
        this.picker = { open: false, ids: [] };
        this.viewerImageId = null;
        this.viewerExpanded = false;
        this.viewerTab = VIEWER_360;
        if (this.isDone) await store.dispatch('AI_ANALYSIS/LOAD_FINDINGS', this.jobId);
      },
      back() {
        this.$router.push(this.listRoute);
      },
      scanNameOf(finding) {
        return (this.imagesById[finding.imageId] || {}).name || '';
      },
      /** Row click, its ⋮ or a badge: activate the area, open its panel and turn the viewer to it. */
      open(finding) {
        this.activeId = finding.id;
        this.showOnScan(finding);
      },
      closePanel() {
        const id = this.activeId;
        this.activeId = null;
        // Focus returns to the row's ⋮ (Registers behaviour).
        this.$nextTick(() => {
          const button = this.$el.querySelector(`tr[data-id="${id}"] .row-more`);
          if (button) button.focus();
        });
      },
      stepPanel(direction) {
        const next = this.findings[this.activeIndex + direction];
        if (next) this.open(next);
      },
      showOnScan(finding) {
        if (finding.imageId && finding.imageId !== this.currentImageId) this.viewerImageId = finding.imageId;
        this.viewerTab = VIEWER_360;
        this.$nextTick(() => {
          const section = this.$refs.viewersSection;
          if (section) section.focusInspect(finding);
        });
      },
      selectScan(imageId) {
        this.viewerImageId = imageId;
      },
      /** Polygon or badge click in the viewer selects the row. */
      onPolygonSelected(inspect) {
        if (!inspect || !inspect.id) return;
        this.activeId = inspect.id;
        this.$nextTick(() => {
          const row = this.$el.querySelector(`tr[data-id="${inspect.id}"]`);
          if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
        });
      },
      // In-page expansion keeps the report and its polygons mounted; the dashboard fullscreen mode would
      // route its "back" action by the AI Inspect route name.
      toggleViewerExpanded() {
        this.viewerExpanded = !this.viewerExpanded;
      },
      onViewerClose() {
        this.viewerExpanded = false;
      },
      openFindingReport(finding) {
        if (finding.reportUrl) {
          window.open(finding.reportUrl, '_blank', 'noopener');
          return;
        }
        this.$dialog.notify.info(FINDING_REPORT_MOCK_MESSAGE);
      },
      label(finding) {
        return `Finding #${finding.badgeIndex}`;
      },
      /** Confirm text for Accept all: the batch size, the consequence, and how many findings are below the ring's low band. */
      acceptAllPrompt(count) {
        const lowConfidence = this.pending.filter((f) => ringBand(f.detectionConfidence) === 'low').length;
        const areas = count === 1 ? 'finding' : 'findings';
        const tail = lowConfidence
          ? ` ${lowConfidence} of them ${lowConfidence === 1 ? 'is' : 'are'} below ${RING_BANDS.MID}% detection confidence.`
          : '';
        return `Accept all ${count} pending ${areas} without reviewing them individually? They will be ${ACCEPTED_FINDINGS_NOTE}.${tail}`;
      },
      confirm(title, text, { cancel = 'Keep', color = 'error' } = {}) {
        return this.$dialog.confirm({
          title,
          text,
          actions: { false: { text: cancel, color: 'secondary' }, true: { text: title, color } },
        });
      },
      async run(label, action) {
        this.busy = true;
        try {
          await action();
          return true;
        } catch (err) {
          error(`[AIAnalysisReport] ${label} failed`, err);
          this.$dialog.notify.error(`${label} failed`);
          return false;
        } finally {
          this.busy = false;
        }
      },
      async approve(finding) {
        const ok = await this.run('Accept', () => this.$store.dispatch('AI_ANALYSIS/ACCEPT', { jobId: this.jobId, id: finding.id }));
        if (ok) this.$dialog.notify.success(`${this.label(finding)} accepted`);
      },
      async reject(finding) {
        if (!(await this.confirm('Reject finding', `Reject ${this.label(finding)}? It will be ${REJECT_NOTE}.`))) return;
        const ok = await this.run('Reject', () => this.$store.dispatch('AI_ANALYSIS/REJECT', { jobId: this.jobId, id: finding.id }));
        if (ok) this.$dialog.notify.success(`${this.label(finding)} rejected — ${REJECT_NOTE}`);
      },
      async approveMany(ids) {
        const ok = await this.run('Accept selected', async () => {
          for (const id of ids) await this.$store.dispatch('AI_ANALYSIS/ACCEPT', { jobId: this.jobId, id });
        });
        if (ok) {
          this.selected = [];
          this.$dialog.notify.success(`${ids.length} ${ids.length === 1 ? 'finding' : 'findings'} accepted`);
        }
      },
      async rejectMany(ids) {
        if (!(await this.confirm('Reject selected', `Reject ${ids.length} ${ids.length === 1 ? 'finding' : 'findings'}? They will be ${REJECT_NOTE}.`))) return;
        const ok = await this.run('Reject selected', async () => {
          for (const id of ids) await this.$store.dispatch('AI_ANALYSIS/REJECT', { jobId: this.jobId, id });
        });
        if (ok) {
          this.selected = [];
          this.$dialog.notify.success(`${ids.length} ${ids.length === 1 ? 'finding' : 'findings'} rejected — ${REJECT_NOTE}`);
        }
      },
      async acceptAll() {
        const count = this.pending.length;
        // Accepting the whole batch skips the per-area review this screen exists for, so it confirms like Reject all
        // does, and names how many of the findings the model itself is unsure about.
        if (!(await this.confirm('Accept all', this.acceptAllPrompt(count), { cancel: 'Cancel', color: 'primary' }))) return;
        const ok = await this.run('Accept all', () => this.$store.dispatch('AI_ANALYSIS/ACCEPT_ALL', this.jobId));
        if (ok) this.$dialog.notify.success(`All ${count} findings accepted — ${ACCEPTED_FINDINGS_NOTE}`);
      },
      async rejectAll() {
        const count = this.pending.length;
        if (!(await this.confirm('Reject all', `Reject all ${count} pending findings? They will be ${REJECT_NOTE}.`))) return;
        const ok = await this.run('Reject all', () => this.$store.dispatch('AI_ANALYSIS/REJECT_ALL', this.jobId));
        if (ok) this.$dialog.notify.success(`All ${count} findings rejected — removed from the report`);
      },
      /* ---- Component link (finding -> master tag) ---- */
      openPicker(ids) {
        if (!ids || !ids.length) return;
        this.picker = { open: true, ids: [...ids] };
      },
      closePicker() {
        this.picker = { ...this.picker, open: false };
      },
      linkSummary(ids, component) {
        const one = ids.length === 1 ? this.findings.find((f) => f.id === ids[0]) : null;
        const subject = one ? this.label(one) : `${ids.length} findings`;
        return component ? `${subject} linked to ${component.name}` : `Link removed from ${one ? this.label(one).toLowerCase() : `${ids.length} findings`}`;
      },
      async linkFindings(ids, component) {
        this.linking = true;
        try {
          const ok = await this.run(component ? 'Link to component' : 'Remove link', () =>
            this.$store.dispatch('AI_ANALYSIS/LINK_COMPONENT', { jobId: this.jobId, ids, component }),
          );
          if (!ok) return false;
          if (component) this.recentComponents = pushRecent(this.recentComponents, component);
          this.$dialog.notify.success(this.linkSummary(ids, component));
          return true;
        } finally {
          this.linking = false;
        }
      },
      /** Removing or replacing a link is a negative action (Khalid, 2026-09-07): it is confirmed like Reject is. */
      confirmUnlink(findings) {
        const one = findings.length === 1 ? findings[0] : null;
        const subject = one ? `${this.label(one)} and ${one.masterTagName || 'its component'}` : `${findings.length} findings and their components`;
        return this.confirm('Remove link', `Remove the link between ${subject}? ${UNLINK_NOTE}`);
      },
      confirmReplace(replaced, total, component) {
        const subject = replaced === total ? `all ${total} selected findings` : `${replaced} of the ${total} selected findings`;
        return this.confirm('Replace links', `${subject} ${replaced === 1 ? 'is' : 'are'} already linked. Replace those links with ${component.name}?`, { cancel: 'Keep', color: 'primary' });
      },
      /** The picker's Link: applies to every finding it was opened for, then closes it and clears a bulk selection. */
      async linkTo(component) {
        const ids = [...this.picker.ids];
        const replaced = linkedCount(this.pickerFindings.filter((f) => f.masterTagId !== component.id));
        if (ids.length > 1 && replaced && !(await this.confirmReplace(replaced, ids.length, component))) return;
        if (await this.linkFindings(ids, component)) {
          this.closePicker();
          if (ids.length > 1) this.selected = [];
        }
      },
      async unlinkFromPicker() {
        const ids = [...this.picker.ids];
        if (await this.unlinkFindings(ids)) this.closePicker();
      },
      /** Clears the link on the given findings after confirmation; those without one are skipped so the toast counts what changed. */
      async unlinkFindings(ids) {
        const linked = this.findings.filter((f) => ids.includes(f.id) && f.masterTagId);
        if (!linkedCount(linked)) return false;
        if (!(await this.confirmUnlink(linked))) return false;
        return this.linkFindings(linked.map((f) => f.id), null);
      },
      linkSuggested(finding) {
        if (this.suggestedTag) this.linkFindings([finding.id], this.suggestedTag);
      },
      /**
       * Builds the PDF here rather than opening the browser's print dialog (Khalid, 2026-09-07): a print
       * produces whatever the user's page setup says and cannot be attached to a record, while a generated
       * document is the same every time. See utils/reportPdf.js.
       */
      async exportPdf() {
        if (this.exporting) return;
        this.exporting = true;
        try {
          const result = await generateReportPdf(this.pdfModel);
          if (result.imageIncluded) {
            this.$dialog.notify.success(`Report exported as ${result.fileName}`);
          } else {
            // Worth having without the picture, but the user must know it is missing and why.
            this.$dialog.notify.warning(`${result.fileName} exported without the annotated image. ${result.imageError}`);
          }
        } catch (err) {
          error('[AI Analysis] the report PDF could not be generated', err);
          this.$dialog.notify.error('The report could not be exported. Please try again.');
        } finally {
          this.exporting = false;
        }
      },
    },
  };
</script>

<style lang="scss">
  @import '../components/aiAnalysis/aiAnalysisTokens';

  // Unscoped on purpose: the report cards (.card, .card-title) are shared by the child components.
  // Every selector is rooted at .ai-report so nothing leaks.
  .ai-report {
    @include ai-analysis-tokens;
    --aa-page: #ffffff;
    // Vuetify dialogs sit at 202; the expanded viewer stays under them.
    --z-viewer-expanded: 200;
    // The 360 viewer's own navbar is absolute at z-index 90, so the sticky action bar has to clear it or the
    // viewer's zoom strip paints over Accept all / Reject all while the report is part-scrolled. It must stay
    // BELOW the app's navigation drawer, which is fixed at 100: at 100 the bar tied with the drawer and won on
    // DOM order, painting the report's buttons over the expanded side panel (Khalid, 2026-09-07).
    --z-action-bar: 95;

    display: grid;
    gap: var(--ds-space-sm);
    // No bottom padding: the sticky action bar must sit flush on the scroll box edge, otherwise scrolled content
    // shows in the padding strip under it.
    padding: var(--ds-space-sm) var(--ds-space-md) 0;
    // The Analysis sheet is h-100 and clips: the report scrolls inside itself (Khalid, 2026-09-07: the footer was cut),
    // and the sticky action bar then sticks to the bottom of this scroll box instead of covering the footer.
    align-content: start;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    color: var(--aa-text-primary);
    font-family: var(--ds-font-family-body);

    .card {
      background: var(--aa-surface);
      border: 1px solid var(--aa-border-default);
      border-radius: var(--ds-radius-sm);
      min-width: 0;
    }
    .card-title {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--ds-space-sm);
      margin: 0;
      padding: 10px var(--ds-space-md);
      font-family: var(--ds-font-family-heading);
      font-size: var(--ds-font-body-sm);
      font-weight: 600;
      color: var(--aa-section-title);

      .ttl { letter-spacing: 0.06em; text-transform: uppercase; }
      .spacer { flex: 1 1 auto; }
      .scope-suffix { font-weight: 500; color: var(--aa-text-muted); font-family: var(--ds-font-family-body); margin-left: -2px; }
    }

    .topline { display: flex; align-items: center; gap: var(--ds-space-sm); min-width: 0; }
    // Tonal back button: Vuetify 2 has no tonal variant, so `outlined` supplies the primary border and this class
    // the tint. Hover is Vuetify's own ::before overlay, which is already currentColor (primary) - no extra rule.
    .topline__back.v-btn { text-transform: none; letter-spacing: 0.02em; font-weight: 600; background: var(--aa-surface-active); }

    .crumbs { min-width: 0; }
    .crumbs ol { display: flex; align-items: center; gap: var(--ds-space-xs); margin: 0; padding: 0; list-style: none; min-width: 0; }
    .crumbs li { display: inline-flex; align-items: center; min-width: 0; }
    .crumb-link {
      padding: 2px 4px;
      margin: -2px -4px;
      border-radius: var(--ds-radius-sm);
      font-size: var(--ds-font-body-sm);
      color: var(--aa-text-secondary);
      text-decoration: none;
      white-space: nowrap;

      &:hover, &:focus-visible { color: var(--v-primary-base); text-decoration: underline; text-underline-offset: 2px; }
    }
    .crumb-section { font-size: var(--ds-font-body-sm); color: var(--aa-text-muted); white-space: nowrap; }
    .crumb-sep .v-icon { color: var(--aa-text-muted); }
    .crumb-current { font-size: var(--ds-font-body-sm); font-weight: 600; color: var(--aa-text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .crumb-kind { font-weight: 400; color: var(--aa-text-muted); }

    /* Body grid: the viewer sets the row height; the left column stretches to it and its findings card takes
       whatever the summary leaves (Khalid, 2026-09-06: the table card fits the available height). */
    .report-body { display: grid; grid-template-columns: minmax(0, 48fr) minmax(0, 52fr); gap: var(--ds-space-sm); align-items: stretch; }
    // With the panel open the table lane needs room, so the left column takes 60 / 40 (Khalid, 2026-09-06).
    .report-body--panel-open { grid-template-columns: minmax(0, 60fr) minmax(0, 40fr); }
    .report-left { display: grid; grid-template-rows: auto minmax(0, 1fr); gap: var(--ds-space-sm); min-width: 0; min-height: 0; }

    .viewer-card { display: flex; flex-direction: column; min-width: 0; }
    .viewer-card__stage { flex: 1 1 auto; height: clamp(480px, 72vh, 820px); min-height: 0; border-top: 1px solid var(--aa-border-default); background: var(--aa-surface-subtle); }
    // The report never closes its viewer, and ViewersSection's close would also flip the shared viewer type to 2D.
    .viewer-card .viewer-control-btn--close { display: none; }
    // The report supplies its own "Annotated image" title and scan selector, and the job fixes the viewer to the
    // 360 tab, so ViewersSection's header (title, 3D/2D/360 tabs, expand) is redundant here. Report grid only.
    .viewer-card .viewer-header { display: none; }
    .viewer-card--expanded { position: fixed; inset: 0; z-index: var(--z-viewer-expanded); border: none; border-radius: 0; }
    .viewer-card--expanded .viewer-card__stage { height: auto; }
    .scan-select { display: inline-flex; align-items: center; min-width: 240px; max-width: 320px; font-family: var(--ds-font-family-body); font-weight: 400; letter-spacing: 0; text-transform: none; }
    .scan-select__field { font-size: var(--ds-font-body-sm); }

    .state-block {
      padding: var(--ds-space-xl);
      text-align: center;
      h3 { font-size: var(--ds-font-subtitle); font-weight: 600; margin: 0 0 var(--ds-space-xs); }
      p { margin: 0 auto; max-width: 520px; font-size: var(--ds-font-body-sm); color: var(--aa-text-muted); line-height: 1.6; }
    }
    .state-block__icon { display: inline-flex; align-items: center; justify-content: center; width: 56px; height: 56px; margin-bottom: var(--ds-space-md); border-radius: 50%; background: var(--aa-surface-subtle); .v-icon { color: var(--aa-text-disabled); } }
    .state-block__chip { margin-bottom: var(--ds-space-sm); }

    .print-footnote { display: none; padding: var(--ds-space-sm) 0 0; font-size: var(--ds-font-caption); color: var(--aa-text-muted); }

    @media (min-width: 1800px) {
      .report-body { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
      .report-body--panel-open { grid-template-columns: minmax(0, 56fr) minmax(0, 44fr); }
    }
    @media (max-width: 1500px) {
      .report-body { grid-template-columns: minmax(0, 1fr); }
    }
  }

  .theme--dark .ai-report {
    @include ai-analysis-tokens-dark;
    --aa-page: #121212;
  }

  /* ---------------------------------------------------------------------------------------------------
     Print (A4 landscape) - ported from the approved print variant of
     src/assets/showcase/ai-analysis-report-showcase.html. Ctrl+P is no longer the export path (that is
     utils/reportPdf.js), so this no longer carries the annotated image; it only keeps a browser print of the
     page legible.
     Selectors are prefixed with .ai-report so they outrank the sticky/frozen rules in _engineeringGrid.scss.
     --------------------------------------------------------------------------------------------------- */
  @media print {
    @page { size: A4 landscape; margin: 10mm; }

    .ai-report {
      // Confidence rings, severity chips and contour colours carry the meaning; without this the browser
      // drops them and the printed report becomes unreadable.
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
      height: auto;
      overflow: visible;
      padding: 0;
      background: #fff;
    }

    /* The app shell prints too unless told not to: the fixed top bar, the left rail, the Analysis tab
       strip, and the Vuetify padding that reserves room for them. These sit outside .ai-report, so the rules
       are global - acceptable because this stylesheet only exists while the report is mounted, and it only
       applies to print. #dashboard-tabs / #dashboard-wrapper are stable ids from views/index.vue. */
    .v-system-bar.top-nav-bar,
    .v-navigation-drawer,
    .v-overlay,
    #dashboard-tabs { display: none !important; }
    .v-main, #dashboard-wrapper { padding: 0 !important; }
    .v-application { background: #fff !important; }

    .ai-report .print-footnote { display: block; }

    /* Screen-only chrome: navigation, controls and anything that cannot print. */
    .ai-report .topline,
    .ai-report .action-bar,
    .ai-report .viewer-card,
    .ai-report .scan-select,
    .ai-report .density-toggle,
    .ai-report .grp-toggle,
    .ai-report .bulk-bar,
    .ai-report .table-refresh,
    .ai-report .detail-panel,
    .ai-report .row-actions,
    .ai-report .col-select,
    .ai-report .col-actions { display: none !important; }

    .ai-report .card { break-inside: avoid; box-shadow: none; }
    .ai-report .report-body { grid-template-columns: minmax(0, 1fr); }
    .ai-report .card-body--with-panel { display: block; }

    /* The table scrolls on screen and must not on paper; the frozen columns un-stick with it. */
    .ai-report .table-wrap { max-height: none; overflow: visible; isolation: auto; }
    .ai-report table.eg { min-width: 0; font-size: 11px; }
    .ai-report table.eg thead th,
    .ai-report table.eg th.col-select,
    .ai-report table.eg td.col-select,
    .ai-report table.eg th.col-id,
    .ai-report table.eg td.col-id,
    .ai-report table.eg th.col-location,
    .ai-report table.eg td.col-location { position: static; box-shadow: none; }
    .ai-report table.eg thead th { font-size: 11px; }
    .ai-report table.eg tbody td { height: 34px; font-size: 11px; white-space: normal; }
    .ai-report table.eg tbody tr { break-inside: avoid; }
    // Grouping stays a screen affordance: a group row's filler colspan counts the columns hidden above, so
    // printing it would overrun the row. The findings print flat, in the sort order on screen.
    .ai-report table.eg tr.group-row { display: none; }
    .ai-report col.c-select, .ai-report col.c-actions { display: none; }

  }
</style>
